<template>
  <div class="bg-white rounded-3xl shadow-md py-8 md:py-12 px-7 md:px-10">
    <p
      class="font-display text-primary text-lg lg:text-2xl xl:text-3xl font-bold mb-2 md:mb-7"
    >
      {{ $t("home.banner.form.title") }}
    </p>
    <p class="text-sm md:text-base text-brand-gray mb-5 md:mb-10">
      {{ $t("home.banner.form.description") }}
    </p>
    <CoreInput
      id="email"
      v-model="email"
      placeholder="Email *"
      required
      @blur="validateEmail"
      @focus="clearErrors"
    />
    <div v-if="inputEmailWarning" class="text-yellow-500 text-sm">
      <span v-html="inputEmailWarning"></span>
    </div>
    <CoreInputError :error="get(errors, 'email')" />
    <CoreButton
      :class="{
        'opacity-70': loading,
      }"
      v-bind:disabled="loading"
      class="w-full mt-6 md:mt-10"
      @click="verifyEmail"
      >{{ $t("home.banner.form.button") }}
    </CoreButton>
    <CoreError v-if="error" :error="error"></CoreError>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { useRouter, useRoute, useCookie } from "#imports";
import * as yup from "yup";
import { get } from "lodash-es";

const { t, locale } = useI18n();

// yup schema
const schema = yup
  .string()
  .trim()
  .email()
  .label("Email")
  .required(t("validation.required", { field: "Email" }));

const email = ref("");
const errors = reactive({ email: null });
const error = ref("");
const loading = ref(false);
const inputEmailWarning = ref("");

const router = useRouter();
const route = useRoute();

// get email from cookie
onMounted(() => {
  const emailCookie = useCookie("email");
  email.value = emailCookie.value ?? "";

  // Clear customer_id cookie if it is already set
  const customerIdCookie = useCookie("customer_id");
  customerIdCookie.value = null;

  document.addEventListener("input-email-warning", (event) => {
    const { detail } = event;
    inputEmailWarning.value = detail.message;
  });
});

async function validateEmail() {
  try {
    await schema.validate(email.value);
    errors.email = null;
  } catch (err) {
    errors.email = err.statusMessage ?? t("validation.invalid", { field: "Email" });
  }
}

// verify email via API
async function verifyEmail() {
  try {
    await schema.validate(email.value);
    errors.email = null;
  } catch (err) {
    errors.email = err.message ?? t("validation.invalid", { field: "Email" });
    return;
  }

  loading.value = true;

  try {
    const response = await $fetch("/verify-email", {
      method: "POST",
      body: { email: email.value, locale: locale.value },
    });

    loading.value = false;
    error.value = "";

    const emailCookie = useCookie("email", {
      expires: new Date(Date.now() + 30 * 60000), // 30 minutes
    });

    emailCookie.value = email.value;

    if (response.customer_id) {
      const customerIdCookie = useCookie("customer_id", {
        expires: new Date(new Date().getTime() + 30 * 60000),
      });
      customerIdCookie.value = response.customer_id;
    }

    router.push(`/registration-card${route.fullPath}`);
  } catch (err) {
    loading.value = false;
    error.value = err.response?._data?.data.statusMessage ?? t("errors.generic");
  }
}

function clearErrors() {
  errors.email = null;
  inputEmailWarning.value = "";
}
</script>
